<template>
  <div class="auth-container">
    <div class="auth-card">
      <!-- Success State: Email de Verificação Enviado -->
      <div v-if="registrationSuccess" class="success-state">
        <div class="success-icon">
          <svg
            class="h-8 w-8 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>

        <h1 class="success-title">Verifique seu Email</h1>

        <p class="success-message">
          Enviamos um link de verificação para
          <strong>{{ registeredEmail }}</strong>
        </p>

        <div class="info-box">
          <p class="text-sm text-gray-700">
            <strong>Próximos passos:</strong>
          </p>
          <ol
            class="list-decimal list-inside text-sm text-gray-600 mt-2 space-y-1"
          >
            <li>Verifique sua caixa de entrada</li>
            <li>Clique no link de verificação</li>
            <li>Faça login e adicione sua foto de perfil</li>
          </ol>
        </div>

        <div class="action-buttons">
          <button
            @click="resendVerificationEmail"
            :disabled="resending"
            class="btn-secondary"
          >
            <svg
              v-if="!resending"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <div v-else class="spinner"></div>
            {{ resending ? "Enviando..." : "Reenviar Email" }}
          </button>

          <router-link to="/login" class="btn-primary">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
              />
            </svg>
            Ir para Login
          </router-link>
        </div>
      </div>

      <!-- Registration Form -->
      <div v-else>
        <!-- Logo -->
        <div class="logo-container">
          <router-link to="/" class="logo-main">
            <img
              src="/logo-no-bg.png"
              alt="BlogDavid Logo"
              class="logo-image"
            />
          </router-link>
        </div>
        <div class="auth-header">
          <h1 class="auth-title">Criar sua conta</h1>
          <p class="auth-subtitle">Comece a compartilhar suas ideias hoje</p>
        </div>

        <form class="auth-form" @submit.prevent="handleSubmit">
          <!-- Username -->
          <div class="form-group">
            <label for="username" class="form-label">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              Nome de usuário
            </label>
            <input
              id="username"
              v-model="form.username"
              type="text"
              required
              class="form-input"
              placeholder="seu_usuario"
              :disabled="authStore.isLoading"
              autocomplete="username"
            />
          </div>

          <!-- Email -->
          <div class="form-group">
            <label for="email" class="form-label">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                />
              </svg>
              Email
            </label>
            <input
              id="email"
              v-model="form.email"
              type="email"
              required
              class="form-input"
              placeholder="seu@email.com"
              :disabled="authStore.isLoading"
              autocomplete="email"
            />
          </div>

          <!-- Password -->
          <div class="form-group">
            <label for="password" class="form-label">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              Senha
            </label>
            <div class="password-input-wrapper">
              <input
                id="password"
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                required
                minlength="6"
                class="form-input"
                placeholder="Mínimo 6 caracteres"
                :disabled="authStore.isLoading"
                autocomplete="new-password"
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="password-toggle-btn"
                :disabled="authStore.isLoading"
              >
                <svg
                  v-if="showPassword"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                  />
                </svg>
                <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              </button>
            </div>
          </div>

          <!-- Confirm Password -->
          <div class="form-group">
            <label for="confirmPassword" class="form-label">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Confirmar senha
            </label>
            <div class="password-input-wrapper">
              <input
                id="confirmPassword"
                v-model="form.confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                required
                class="form-input"
                :class="{
                  'input-error':
                    form.confirmPassword &&
                    form.password !== form.confirmPassword,
                }"
                placeholder="Digite a senha novamente"
                :disabled="authStore.isLoading"
                autocomplete="new-password"
              />
              <button
                type="button"
                @click="showConfirmPassword = !showConfirmPassword"
                class="password-toggle-btn"
                :disabled="authStore.isLoading"
              >
                <svg
                  v-if="showConfirmPassword"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                  />
                </svg>
                <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              </button>
            </div>
            <p
              v-if="
                form.confirmPassword && form.password !== form.confirmPassword
              "
              class="input-error-message"
            >
              As senhas não coincidem
            </p>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="authStore.isLoading || !isFormValid"
            class="btn-primary"
          >
            <span v-if="authStore.isLoading" class="btn-loading">
              <div class="spinner"></div>
              Criando conta...
            </span>
            <span v-else class="btn-content">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                />
              </svg>
              Criar Conta
            </span>
          </button>

          <!-- Footer Link -->
          <p class="auth-footer">
            Já tem uma conta?
            <router-link to="/login" class="auth-link"> Entrar </router-link>
          </p>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, computed, ref } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/store/auth";

export default {
  name: "Register",
  setup() {
    const router = useRouter();
    const authStore = useAuthStore();

    // New state for verification flow
    const registrationSuccess = ref(false);
    const registeredEmail = ref("");
    const resending = ref(false);

    // Password visibility toggles
    const showPassword = ref(false);
    const showConfirmPassword = ref(false);

    const form = reactive({
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
    });

    const isFormValid = computed(() => {
      return (
        form.username.trim() &&
        form.email.trim() &&
        form.password.length >= 6 &&
        form.password === form.confirmPassword
      );
    });

    const handleSubmit = async () => {
      if (!isFormValid.value) {
        console.log("❌ Formulário inválido");
        return;
      }

      console.log("📤 Enviando dados de registro:", {
        username: form.username.trim(),
        email: form.email.trim(),
        passwordLength: form.password.length,
      });

      try {
        const result = await authStore.register({
          username: form.username.trim(),
          email: form.email.trim(),
          password: form.password,
        });

        if (result.success) {
          // Check if verification is needed
          if (
            result.needsVerification ||
            result.message?.includes("Verifique seu email")
          ) {
            registrationSuccess.value = true;
            registeredEmail.value = form.email.trim();
          } else {
            // Old flow - auto login (shouldn't happen with new backend)
            router.push("/");
          }
        }
      } catch (error) {
        console.error("❌ Erro no registro:", error);
      }
    };

    const resendVerificationEmail = async () => {
      resending.value = true;
      try {
        await authStore.resendVerification(registeredEmail.value);
      } finally {
        resending.value = false;
      }
    };

    return {
      form,
      authStore,
      isFormValid,
      handleSubmit,
      registrationSuccess,
      registeredEmail,
      resending,
      resendVerificationEmail,
      showPassword,
      showConfirmPassword,
    };
  },
};
</script>

<style scoped>
.auth-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  padding: 2rem 1rem;
}

.auth-card {
  width: 100%;
  max-width: 440px;
  background: white;
  border-radius: 16px;
  padding: 3rem 2.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

/* Logo */
.logo-container {
  text-align: center;
  margin-bottom: 2rem;
}

.logo-main {
  display: inline-block;
  transition: transform 0.2s;
}

.logo-main:hover {
  transform: scale(1.05);
}

.logo-image {
  height: 60px;
  width: auto;
  object-fit: contain;
}

/* Success State */
.success-state {
  text-align: center;
}

.success-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  background: #22c55e;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  animation: scaleIn 0.5s ease-out;
}

.success-icon svg {
  width: 44px;
  height: 44px;
}

@keyframes scaleIn {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.success-title {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 1rem;
}

.success-message {
  font-size: 1rem;
  color: #64748b;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.success-message strong {
  color: #1a202c;
  font-weight: 600;
}

.info-box {
  display: flex;
  gap: 1rem;
  padding: 1.25rem;
  background: #f0fdf4;
  border: 2px solid #bbf7d0;
  border-radius: 12px;
  text-align: left;
  margin-bottom: 2rem;
}

.info-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  background: #22c55e;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.info-icon svg {
  width: 22px;
  height: 22px;
}

.info-content {
  flex: 1;
}

.info-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #15803d;
  margin-bottom: 0.5rem;
}

.info-steps {
  font-size: 0.875rem;
  color: #15803d;
  padding-left: 1.25rem;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/* Header */
.auth-header {
  text-align: center;
  margin-bottom: 2.5rem;
}

.auth-title {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.5rem;
}

.auth-subtitle {
  font-size: 1rem;
  color: #64748b;
}

/* Form */
.auth-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a202c;
}

.form-label svg {
  width: 18px;
  height: 18px;
  color: #3b82f6;
}

.form-input {
  width: 100%;
  padding: 0.875rem 1rem;
  font-size: 1rem;
  color: #1a202c;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  outline: none;
  transition: all 0.2s;
}

.form-input:focus {
  background: white;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input.input-error {
  border-color: #ef4444;
}

.form-input.input-error:focus {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.form-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.form-input::placeholder {
  color: #94a3b8;
}

.input-error-message {
  font-size: 0.875rem;
  color: #ef4444;
  margin: 0;
}

/* Password Input Wrapper */
.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-wrapper .form-input {
  padding-right: 3rem;
}

.password-toggle-btn {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: color 0.2s;
  border-radius: 6px;
}

.password-toggle-btn:hover:not(:disabled) {
  color: #3b82f6;
  background: #f1f5f9;
}

.password-toggle-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.password-toggle-btn svg {
  width: 20px;
  height: 20px;
}

/* Buttons */
.btn-primary {
  width: 100%;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 600;
  color: white;
  background: #3b82f6;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-decoration: none;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.btn-primary:active:not(:disabled) {
  transform: translateY(0);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  width: 100%;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 600;
  color: #1a202c;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-secondary:hover:not(:disabled) {
  background: #f1f5f9;
  border-color: #cbd5e1;
  transform: translateY(-2px);
}

.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary svg {
  width: 20px;
  height: 20px;
}

.btn-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-content svg {
  width: 20px;
  height: 20px;
}

.btn-loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.btn-secondary .spinner {
  border-color: rgba(26, 32, 44, 0.3);
  border-top-color: #1a202c;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Footer Link */
.auth-footer {
  text-align: center;
  font-size: 0.875rem;
  color: #64748b;
  margin-top: 0.5rem;
}

.auth-link {
  color: #3b82f6;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.2s;
}

.auth-link:hover {
  color: #2563eb;
  text-decoration: underline;
}

/* Responsive */
@media (max-width: 480px) {
  .auth-card {
    padding: 2rem 1.5rem;
  }

  .auth-title,
  .success-title {
    font-size: 1.5rem;
  }
}
</style>
